class HdtBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.pollTimer = null;
    this.driftObserver = null;
    this.lastVariantId = "";
    this.selection = new Map();
    this.lightboxOpen = false;
  }

  connectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.pollTimer) clearInterval(this.pollTimer);

    this.abortController = new AbortController();
    this.config = this.readConfig();
    if (!this.config) return;

    this.form = document.getElementById(this.dataset.formId || "");
    this.variantInput = this.form?.querySelector('[name="id"]');
    this.variantPicker = document.querySelector(`hdt-variant-picker[form="${this.dataset.formId || ""}"]`);
    this.originalAddButton = this.form?.querySelector('button[name="add"]');
    this.customSubmit = this.querySelector("[data-bundle-submit]");
    this.stickySubmit = this.querySelector("[data-bundle-submit-sticky]");
    this.messageEl = this.querySelector("[data-bundle-message]");
    this.variantTitleEl = this.querySelector("[data-bundle-variant-title]");
    this.variantPriceEl = this.querySelector("[data-bundle-variant-price]");
    this.requiredCountEl = this.querySelector("[data-bundle-required-count]");
    this.selectedCountEl = this.querySelector("[data-bundle-selected-count]");
    this.remainingCountEl = this.querySelector("[data-bundle-remaining-count]");
    this.stickySelectedEl = this.querySelector("[data-bundle-sticky-selected]");
    this.stickyRequiredEl = this.querySelector("[data-bundle-sticky-required]");
    this.progressFillEl = this.querySelector("[data-bundle-progress-fill]");
    this.progressTextEl = this.querySelector("[data-bundle-progress-text]");
    this.compositionEl = this.querySelector("[data-bundle-composition]");
    this.bundleRoot = this.closest(".hdt-product__bundle-builder");
    this.lightbox = this.querySelector("[data-bundle-lightbox]");
    this.lightboxImage = this.querySelector("[data-bundle-lightbox-image]");
    this.lightboxTitle = this.querySelector("[data-bundle-lightbox-title]");
    this.activateBundlePageGuards();
    const movedForLayout = this.applyLayoutMode();
    if (movedForLayout) return;

    this.propertyMount = document.createElement("div");
    this.propertyMount.hidden = true;
    this.form?.appendChild(this.propertyMount);

    if (!this.form || !this.variantInput || !this.customSubmit) return;

    if (this.config.hideDefaultAtc && this.originalAddButton) {
      this.originalAddButton.classList.add("hdt-bundle-builder__atc-hidden");
      this.originalAddButton.setAttribute("aria-hidden", "true");
      this.originalAddButton.tabIndex = -1;
    }

    this.bindItemControls();
    this.bindVariantControls();
    this.bindLightboxControls();
    this.bindSubmitControls();

    this.form.addEventListener(
      "submit",
      (event) => {
        if (!this.isValidSelection()) {
          event.preventDefault();
          this.renderMessage("Select the exact required quantity to continue.", true);
          return;
        }
        this.injectLineItemProperties();
      },
      { signal: this.abortController.signal }
    );

    this.variantInput.addEventListener("change", () => this.handleVariantChange(true), {
      signal: this.abortController.signal,
    });

    document.addEventListener("variant:update", () => this.handleVariantChange(true), {
      signal: this.abortController.signal,
    });
    document.addEventListener("variant:selected", () => this.handleVariantChange(true), {
      signal: this.abortController.signal,
    });

    this.lastVariantId = this.currentVariantId();
    this.pollTimer = window.setInterval(() => {
      const now = this.currentVariantId();
      if (now !== this.lastVariantId) this.handleVariantChange(true);
    }, 250);

    this.handleVariantChange(false);
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.pollTimer) clearInterval(this.pollTimer);
    if (this.driftObserver) this.driftObserver.disconnect();
    if (!document.querySelector("hdt-bundle-builder")) {
      document.documentElement.classList.remove("hdt-bundle-builder-page");
    }
    this.closeLightbox();
  }

  readConfig() {
    const script = this.querySelector('[type="application/json"][data-bundle-config]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  bindSubmitControls() {
    const submitHandler = () => {
      if (!this.isValidSelection()) {
        this.renderMessage("Select the exact required quantity to continue.", true);
        return;
      }
      this.injectLineItemProperties();
      if (typeof this.form.requestSubmit === "function") {
        this.form.requestSubmit(this.originalAddButton || undefined);
      } else {
        this.form.submit();
      }
    };

    this.customSubmit.addEventListener("click", submitHandler, { signal: this.abortController.signal });
    if (this.stickySubmit) {
      this.stickySubmit.addEventListener("click", submitHandler, { signal: this.abortController.signal });
    }
  }

  bindItemControls() {
    const increaseButtons = this.querySelectorAll("[data-bundle-increase]");
    const decreaseButtons = this.querySelectorAll("[data-bundle-decrease]");

    increaseButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          this.changeItemQuantity(button.dataset.itemId, 1);
        },
        { signal: this.abortController.signal }
      );
    });

    decreaseButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          this.changeItemQuantity(button.dataset.itemId, -1);
        },
        { signal: this.abortController.signal }
      );
    });
  }

  bindVariantControls() {
    const variantButtons = this.querySelectorAll("[data-bundle-variant-option]");
    variantButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          this.selectVariant(button.dataset.variantId);
        },
        { signal: this.abortController.signal }
      );
    });
  }

  bindLightboxControls() {
    const openButtons = this.querySelectorAll("[data-bundle-open-lightbox]");
    openButtons.forEach((button) => {
      button.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.openLightbox(button.dataset.lightboxSrc, button.dataset.lightboxTitle || "");
        },
        { signal: this.abortController.signal }
      );
    });

    const closeButtons = this.querySelectorAll("[data-bundle-lightbox-close]");
    closeButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => this.closeLightbox(),
        { signal: this.abortController.signal }
      );
    });

    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape" && this.lightboxOpen) this.closeLightbox();
      },
      { signal: this.abortController.signal }
    );
  }

  activateBundlePageGuards() {
    document.documentElement.classList.add("hdt-bundle-builder-page");
    this.disableExternalZoomContext();
    this.removeZoomArtifacts();

    if (!this.driftObserver && typeof MutationObserver !== "undefined") {
      this.driftObserver = new MutationObserver(() => this.removeZoomArtifacts());
      this.driftObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  disableExternalZoomContext() {
    const sectionRoot = this.closest('[id^="shopify-section-"]');
    if (!sectionRoot) return;

    sectionRoot.querySelectorAll(".zoom-type--external").forEach((node) => {
      node.classList.remove("zoom-type--external");
    });

    sectionRoot.querySelectorAll("hdt-product-media").forEach((media) => {
      media.setAttribute("image-zoom", "no");
      media.setAttribute("zoom-options", '{"type":"none","magnify":1}');
    });
  }

  removeZoomArtifacts() {
    document.querySelectorAll(".drift-bounding-box, .drift-zoom-pane").forEach((node) => {
      node.remove();
    });

    const sectionRoot = this.closest('[id^="shopify-section-"]');
    if (!sectionRoot) return;
    sectionRoot.querySelectorAll('[id^="product-zoom-"], .hdt-produc-zoom--wrapp').forEach((node) => {
      node.style.display = "none";
      node.style.width = "0";
      node.style.height = "0";
      node.style.overflow = "hidden";
    });
  }

  applyLayoutMode() {
    const layoutMode = this.dataset.layoutMode || "full_width";
    if (layoutMode === "inline") return false;
    if (!this.bundleRoot) return;
    if (this.closest(".hdt-main-product-quick-view")) return;

    const sectionRoot = this.closest('[id^="shopify-section-"]');
    if (!sectionRoot) return;
    sectionRoot.classList.add("hdt-bundle-builder-full-width");

    const sectionId = this.dataset.sectionId || "";
    const mainRow = sectionId
      ? sectionRoot.querySelector(`.hdt-secion-main-product--${sectionId}`)
      : sectionRoot.querySelector('[class*="hdt-secion-main-product--"]');
    if (!mainRow || !mainRow.parentNode) return false;

    let mount = sectionRoot.querySelector(`[data-bundle-builder-mount="${sectionId || "default"}"]`);
    if (!mount) {
      mount = document.createElement("div");
      mount.className = "hdt-bundle-builder-mount";
      mount.dataset.bundleBuilderMount = sectionId || "default";
      mainRow.insertAdjacentElement("afterend", mount);
    }

    const wasMoved = this.bundleRoot.parentElement !== mount;
    if (wasMoved) mount.appendChild(this.bundleRoot);
    this.bundleRoot.classList.add("is-layout-full-width");
    this.bundleRoot.classList.remove("hdt-product-info__item");

    const zoomWrapper = sectionId
      ? sectionRoot.querySelector(`#product-zoom-${sectionId}`)
      : sectionRoot.querySelector('[id^="product-zoom-"]');
    if (zoomWrapper) zoomWrapper.style.display = "none";
    return wasMoved;
  }

  openLightbox(src, title) {
    if (!this.lightbox || !this.lightboxImage || !src) return;
    if (this.lightbox.parentNode !== document.body) {
      document.body.appendChild(this.lightbox);
    }
    this.lightboxImage.src = src;
    this.lightboxImage.alt = title || "";
    if (this.lightboxTitle) this.lightboxTitle.textContent = title || "";
    this.lightbox.hidden = false;
    this.lightboxOpen = true;
    document.documentElement.classList.add("hdt-bundle-lightbox-open");
  }

  closeLightbox() {
    if (!this.lightbox) return;
    this.lightbox.hidden = true;
    this.lightboxOpen = false;
    document.documentElement.classList.remove("hdt-bundle-lightbox-open");
  }

  currentVariantId() {
    return String(this.variantInput?.value || "");
  }

  currentVariantRule() {
    const variantId = this.currentVariantId();
    return this.config.variantRules?.[variantId] || null;
  }

  resolveRequiredPicks(rule) {
    if (!rule) return 0;

    if (Number.isFinite(Number(rule.required))) {
      return Math.max(0, parseInt(rule.required, 10));
    }

    if (this.config.enableTitleFallback && typeof rule.title === "string") {
      const match = rule.title.match(/\d+/);
      if (match) return parseInt(match[0], 10);
    }

    return 0;
  }

  currentRequiredPicks() {
    return this.resolveRequiredPicks(this.currentVariantRule());
  }

  selectedTotal() {
    let total = 0;
    this.selection.forEach((value) => {
      total += value;
    });
    return total;
  }

  selectedItems() {
    return (this.config.items || [])
      .map((item) => ({
        id: item.id,
        handle: item.handle,
        title: item.title,
        quantity: this.selection.get(String(item.id)) || 0,
      }))
      .filter((item) => item.quantity > 0);
  }

  selectVariant(variantId) {
    if (!variantId) return;

    if (this.variantPicker && typeof this.variantPicker.selectVariantByID === "function") {
      try {
        this.variantPicker.selectVariantByID(String(variantId), true);
      } catch (error) {
        // Fallback handled below.
      }
    }

    if (String(this.variantInput.value || "") !== String(variantId)) {
      this.variantInput.value = String(variantId);
      this.variantInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    window.setTimeout(() => this.handleVariantChange(true), 0);
  }

  changeItemQuantity(itemId, change) {
    if (!itemId || !change) return;
    const rule = this.currentVariantRule();
    if (rule && rule.available === false) return;
    const required = this.currentRequiredPicks();
    if (required <= 0) return;

    const current = this.selection.get(itemId) || 0;
    const currentTotal = this.selectedTotal();
    let next = current + change;

    if (next < 0) next = 0;
    if (change > 0 && currentTotal >= required) next = current;

    this.selection.set(itemId, next);

    const card = this.querySelector(`[data-bundle-item-card="${itemId}"]`);
    if (card) {
      card.classList.remove("is-changed");
      window.requestAnimationFrame(() => card.classList.add("is-changed"));
      window.setTimeout(() => card.classList.remove("is-changed"), 220);
    }

    this.render();
  }

  resetSelection() {
    this.selection.clear();
    (this.config.items || []).forEach((item) => {
      this.selection.set(String(item.id), 0);
    });
  }

  handleVariantChange(resetSelection) {
    const variantId = this.currentVariantId();
    const shouldReset = resetSelection && this.config.resetOnVariantChange;

    if (shouldReset || !this.selection.size) {
      this.resetSelection();
    } else {
      const required = this.currentRequiredPicks();
      if (this.selectedTotal() > required) this.resetSelection();
    }

    this.lastVariantId = variantId;
    this.render();
  }

  isValidSelection() {
    const required = this.currentRequiredPicks();
    return required > 0 && this.selectedTotal() === required;
  }

  renderMessage(message, isError = false) {
    if (!this.messageEl) return;
    this.messageEl.textContent = message;
    this.messageEl.classList.toggle("is-error", isError);
  }

  renderComposition() {
    if (!this.compositionEl) return;
    const selectedItems = this.selectedItems();
    if (!selectedItems.length) {
      this.compositionEl.textContent = "Nothing selected yet.";
      return;
    }
    this.compositionEl.textContent = selectedItems.map((item) => `${item.title} x${item.quantity}`).join(" | ");
  }

  renderVariantCards() {
    const currentVariantId = this.currentVariantId();
    const cards = this.querySelectorAll("[data-bundle-variant-option]");
    cards.forEach((button) => {
      const isActive = String(button.dataset.variantId || "") === currentVariantId;
      button.setAttribute("aria-pressed", isActive ? "true" : "false");
      const wrapper = button.closest("[data-variant-card]");
      if (wrapper) wrapper.classList.toggle("is-active", isActive);
    });
  }

  renderProgress(selected, required) {
    const progress = required > 0 ? Math.min(selected / required, 1) : 0;
    if (this.progressFillEl) this.progressFillEl.style.width = `${Math.round(progress * 100)}%`;
    if (this.progressTextEl) {
      this.progressTextEl.textContent = required > 0 ? `${selected} of ${required} selected` : "Variant not configured";
    }
  }

  render() {
    const rule = this.currentVariantRule();
    const variantUnavailable = Boolean(rule && rule.available === false);
    const required = this.currentRequiredPicks();
    const selected = this.selectedTotal();
    const remaining = Math.max(required - selected, 0);
    const canSubmit = this.isValidSelection();
    const externalDisabled = Boolean(this.originalAddButton?.disabled);

    if (this.variantTitleEl) this.variantTitleEl.textContent = rule?.title || "Unknown variant";
    if (this.variantPriceEl) this.variantPriceEl.textContent = rule?.price || "";
    if (this.requiredCountEl) this.requiredCountEl.textContent = String(required);
    if (this.selectedCountEl) this.selectedCountEl.textContent = String(selected);
    if (this.remainingCountEl) this.remainingCountEl.textContent = String(remaining);
    if (this.stickySelectedEl) this.stickySelectedEl.textContent = String(selected);
    if (this.stickyRequiredEl) this.stickyRequiredEl.textContent = String(required);

    this.renderVariantCards();
    this.renderProgress(selected, required);
    this.renderComposition();

    (this.config.items || []).forEach((item) => {
      const itemId = String(item.id);
      const qty = this.selection.get(itemId) || 0;
      const qtyEl = this.querySelector(`[data-bundle-item-qty="${itemId}"]`);
      const minusBtn = this.querySelector(`[data-bundle-decrease][data-item-id="${itemId}"]`);
      const plusBtn = this.querySelector(`[data-bundle-increase][data-item-id="${itemId}"]`);
      if (qtyEl) qtyEl.textContent = String(qty);
      if (minusBtn) minusBtn.disabled = qty <= 0 || variantUnavailable;
      if (plusBtn) plusBtn.disabled = required <= 0 || selected >= required || variantUnavailable;
    });

    if (variantUnavailable) {
      this.renderMessage("This variant is sold out.", true);
    } else if (required <= 0) {
      this.renderMessage("This variant is not configured yet. Add required picks in variant metafields.", true);
    } else if (canSubmit) {
      this.renderMessage("Bundle complete. Ready to add to cart.");
    } else {
      this.renderMessage(`Select ${remaining} more to complete this bundle.`);
    }

    const disableSubmit = !canSubmit || externalDisabled || variantUnavailable;
    if (this.customSubmit) this.customSubmit.disabled = disableSubmit;
    if (this.stickySubmit) this.stickySubmit.disabled = disableSubmit;
  }

  injectLineItemProperties() {
    if (!this.propertyMount) return;
    this.propertyMount.innerHTML = "";

    const selectedItems = this.selectedItems();
    const rule = this.currentVariantRule() || {};
    const compositionText = selectedItems.map((item) => `${item.title} x${item.quantity}`).join(", ");
    const machinePayload = {
      variant_id: this.currentVariantId(),
      required_picks: this.currentRequiredPicks(),
      selected_total: this.selectedTotal(),
      items: selectedItems,
    };

    this.addPropertyInput("Bundle Variant", rule.title || this.currentVariantId());
    this.addPropertyInput("Bundle Composition", compositionText);
    this.addPropertyInput("_bundle_builder_payload", JSON.stringify(machinePayload));
  }

  addPropertyInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `properties[${name}]`;
    input.value = value;
    input.dataset.bundleProperty = "true";
    this.propertyMount.appendChild(input);
  }
}

if (!customElements.get("hdt-bundle-builder")) {
  customElements.define("hdt-bundle-builder", HdtBundleBuilder);
}
